import { useEffect, useMemo } from "react";
import useUIBp from "../../../../Hooks/useUIBoilerplate";
import useURLQuery from "../../../../Hooks/useURLQuery";
import { useWorker } from "../../../../Hooks/useWorker";
import { createUsersRequestID } from "../../../../store/actions/recoveries/createUsersRequest";
import { usersDataId } from "../../../../store/actions/users/getAllUsers";
import getGroupData from "../../../../store/actions/users/getGroupData";
import { tunnelsDataId } from "../../../../store/actions/users/getTunnelData";
import getUsersRecursive from "../../../../store/actions/users/getUsersRecursive";
import { UIactions } from "../../../../store/slices/UI";
import { warehouseActions } from "../../../../store/slices/warehouse";
import { workerActions } from "../../../../store/slices/workers";
import { containsEscaped } from "../../../../utility/misc/filter";
import { sortObjByKey } from "../../../../utility/misc/sortObjByKey";
import getC365List from "../../../Reports/Actions/getc365List";
import { recursiveRequestId } from "../../../UI/BasicLayout/Components/FilterPlusActions/Recursive";
import { defaultFilter, defaultPagination, defaultSelected, defaultSort } from "../../../UI/GeneralPurpose/genericIDs";
import CalculatePagination from "../../../UI/Utilities/CalculatePagination";
import Core from "./Components/Core";

const Users = () => {
  const { auth, dispatch, navigate, UI, warehouse } = useUIBp();
  const { group } = useURLQuery();
  const workerId = `groupData${group}`;
  const { done, working, error } = useWorker(workerId);
  const { group: authgroup, admin, user } = auth;
  const filter = UI.filter ?? "";
  const sort = UI.sort ?? { value: "login" };
  const pagination = UI.pagination ?? { page: 0, count: 0, rowsPerPage: 0 };
  const recursive = UI[recursiveRequestId];

  const users = useMemo(() => {
    const users = warehouse[usersDataId] ?? [];
    const tunnels = warehouse[tunnelsDataId] ?? {};
    let usersPlus = [];
    for (const user of users) {
      const connected = tunnels[user.login] ? tunnels[user.login].length : 0;
      const newUser = { ...user, connected };
      usersPlus.push(newUser);
    }
    return usersPlus;
  }, [warehouse]);

  const users2 = useMemo(() => {
    return warehouse[usersDataId] ?? [];
  }, [warehouse]);

  // If user is not an admin move him to his files
  useEffect(() => {
    if (!admin) {
      const url = "/dashboard/files?user=" + encodeURIComponent(user);
      navigate(url, { replace: true });
    }
  }, [navigate, user, admin]);

  // Navigate to base group if no group param is set
  useEffect(() => {
    if (!group && admin) {
      navigate("/dashboard/users?group=" + authgroup, { replace: true });
    }
  }, [dispatch, navigate, group, authgroup, admin]);

  // If group changes clear filters, sort and current worker
  useEffect(() => {
    if (group) {
      dispatch(workerActions.clear(workerId));
      dispatch(UIactions.clear(defaultFilter));
      dispatch(UIactions.clear(defaultSort));
      dispatch(warehouseActions.clear());
    }
  }, [dispatch, group, workerId]);

  // If group is set, but not done? Get data!
  useEffect(() => {
    const controller = new AbortController();
    if (group && !done) {
      const payload = { ...auth, id: workerId, controller, group };
      if (recursive) {
        dispatch(getUsersRecursive(payload));
      } else {
        dispatch(getGroupData(payload));
      }
    }
    return () => {
      controller.abort();
    };
  }, [dispatch, auth, group, done, workerId, recursive]);

  useEffect(() => {
    const users = users2.map((user) => user.login);
    if (users !== 0 && done) {
      dispatch(getC365List({ ...auth, users }));
    }
  }, [dispatch, users2, done, auth]);

  const filteredUsers = useMemo(() => {
    return users
      .filter((user) => containsEscaped(user.login, filter) || containsEscaped(user.name, filter))
      .sort((a, b) => sortObjByKey(a, b, sort.value, sort.descending));
  }, [users, filter, sort.value, sort.descending]);

  // Array to select all
  const allUsers = useMemo(() => {
    return filteredUsers.filter((x) => x.login !== auth.user).map((x) => x.login);
  }, [filteredUsers, auth.user]);

  // Initializing pagination
  useEffect(() => {
    dispatch(UIactions.setPagination({ count: filteredUsers.length }));
  }, [dispatch, filteredUsers.length]);

  // Calculating effective pagination
  const { from, to } = CalculatePagination(pagination);
  const paginatedUsers = useMemo(() => {
    return filteredUsers.filter((x, idx) => from <= idx + 1 && idx < to);
  }, [from, to, filteredUsers]);

  // Garbage collection
  useEffect(() => {
    return () => {
      dispatch(UIactions.clear(defaultFilter));
      dispatch(UIactions.clear(defaultSort));
      dispatch(UIactions.clear(defaultSelected));
      dispatch(UIactions.clear(defaultPagination));
      dispatch(warehouseActions.unload(usersDataId));
      dispatch(warehouseActions.unload(tunnelsDataId));
      dispatch(workerActions.clear(`groupData${group}`));
      dispatch(workerActions.clear(createUsersRequestID));
    };
  }, [dispatch, group]);

  return <Core {...{ users: paginatedUsers, allUsers, done, working, error, workerId }} />;
};

export default Users;
